/**
 * Uniform error shape for the API client. Real transport should translate
 * HTTP error responses into this same shape so every view can handle errors
 * one way regardless of adapter (mock today, HTTP/WS later).
 */
export class ApiError extends Error {
  readonly status: number;
  readonly code: string;

  constructor(message: string, status: number, code: string) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.code = code;
  }
}

export function isNotFound(error: unknown): error is ApiError {
  return error instanceof ApiError && error.status === 404;
}

/**
 * True for a 401 from an authenticated call. `realClient.ts` already fires
 * `emitUnauthorized` (authEvents.ts) the moment this happens, which clears
 * `AuthContext`'s session in the same tick — `ProtectedRoute` then swaps the
 * whole page out for a redirect to `/login` on that same render pass. Any
 * view still holding this error should render its loading state rather than
 * an ErrorState: there's nothing actionable, the operator is already on
 * their way out.
 */
export function isUnauthorized(error: unknown): error is ApiError {
  return error instanceof ApiError && error.status === 401;
}

/**
 * True for a licensing refusal (ADR-0017 §2.3): a NEW action was refused
 * because a licensed limit or a plan-gated feature was reached, never
 * because anything already running was touched (charter §1's safety
 * corollary). Every 402 the server emits carries its own plain-language
 * `message` explaining the limit and how to raise it — callers should
 * render `error.message` verbatim rather than a generic failure, and link
 * to the plan/billing page rather than inventing their own copy.
 */
export function isPaymentRequired(error: unknown): error is ApiError {
  return error instanceof ApiError && error.status === 402;
}

/**
 * True for a 409 — the request was well-formed but refused because of the
 * current state of things (a name collision, a uniqueness constraint, or —
 * for `deployService` specifically, mid ADR-0025 — the deploy path itself
 * being rebuilt on the Placement Graph). None of these are the caller's
 * mistake, so callers should render the server's own `message` as an
 * informational/neutral state rather than an alarming failure (Q-7) — see
 * `ServiceDetailPage`'s deploy handling.
 */
export function isConflict(error: unknown): error is ApiError {
  return error instanceof ApiError && error.status === 409;
}

/**
 * True for a 422 — the request was well-formed JSON but refused as
 * unprocessable given its content (e.g. `promoteAgentSession` on a session
 * with no worktree, or `openPr: true` without a title). Distinct from a 400
 * (malformed request) and a 409 (state conflict): this is "the fields you
 * sent don't add up," so callers should render the server's own message
 * inline next to the offending field(s) rather than a generic failure.
 */
export function isUnprocessable(error: unknown): error is ApiError {
  return error instanceof ApiError && error.status === 422;
}

/**
 * True for a 503 — the control plane deliberately shed the request rather than
 * queue it (charter A-8/P-6: e.g. `auth.Service.acquireHashSlot` bounds
 * concurrent Argon2id verifications and fails fast instead of piling up
 * another 64 MiB hash under load — see `server/internal/auth/service.go`).
 * This is the one status the server itself documents as transient
 * ("server busy, retry shortly"): unlike every other error above, a 503 says
 * nothing about the request itself being wrong, so a caller MAY retry it
 * shortly rather than surface it as a hard failure on the first attempt (see
 * `AuthContext.login`'s bounded retry).
 */
export function isServiceUnavailable(error: unknown): error is ApiError {
  return error instanceof ApiError && error.status === 503;
}

/**
 * True for a capability that is genuinely not built/enabled yet — e.g. the
 * provider-credential/instance endpoints when `COMEBYE_PROVISIONING` is off,
 * which fall through to the server's unauthenticated-route catch-all and
 * answer a plain-text 501. Callers MUST render the server's own message as a
 * persistent, honest "not available yet" state — never retry silently, never
 * treat it as a generic failure, and never assume success happened anyway.
 */
export function isNotImplemented(error: unknown): error is ApiError {
  return error instanceof ApiError && error.status === 501;
}
